'use client';

import { useEffect, useRef } from 'react';

const SCALE_STEP = 0.035;

// Covered cards recede at twice the cover rate, so they visibly shift back
// early in the transition instead of easing back over its whole length.
const RECEDE_RATE = 2;

// The cards pin via position: sticky in CSS, so the stack works without
// JavaScript. This hook measures the lockup and last card so the mobile
// layout can pin the cards below the lockup and size the lockup's sticky box
// to the stack bottom (--lockup-height, --last-card-height), and adds the
// deck effect: as a card slides up over the one before it, the covered card
// scales down in proportion to how far it has been covered (--stack-scale).
export default function useStackedCards() {
  const listRef = useRef<HTMLDivElement>(null);
  const lockupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    let frame = 0;

    const update = () => {
      frame = 0;
      const list = listRef.current;
      if (!list) return;

      const cards = Array.from(list.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement
      );

      const container = list.parentElement;
      const lastCard = cards[cards.length - 1];
      const lockup = lockupRef.current;
      if (container && lastCard && lockup) {
        container.style.setProperty(
          '--last-card-height',
          `${lastCard.offsetHeight}px`
        );
        container.style.setProperty(
          '--lockup-height',
          `${lockup.offsetHeight}px`
        );
      }

      // Pad each card's sticky margin box down to the last card's pinned
      // bottom, so the section end pushes the whole deck out together and the
      // stacked top edges stay visible instead of collapsing (sticky
      // constrains the margin box, not the border box).
      const pinnedTops = cards.map(
        card => parseFloat(getComputedStyle(card).top) || 0
      );
      const lastBottom =
        pinnedTops[cards.length - 1] + (lastCard?.offsetHeight ?? 0);
      cards.forEach((card, index) => {
        const pad = lastBottom - (pinnedTops[index] + card.offsetHeight);
        card.style.setProperty('--stack-exit-pad', `${Math.max(0, pad)}px`);
      });

      if (prefersReducedMotion) return;
      const rects = cards.map(card => card.getBoundingClientRect());

      cards.forEach((card, index) => {
        let coveredDepth = 0;

        for (let next = index + 1; next < cards.length; next += 1) {
          const coverDistance = rects[index].bottom - pinnedTops[next];
          if (coverDistance <= 0) continue;

          const progress =
            ((rects[index].bottom - rects[next].top) / coverDistance) *
            RECEDE_RATE;
          coveredDepth += Math.max(0, Math.min(1, progress));
        }

        card.style.setProperty(
          '--stack-scale',
          `${1 - SCALE_STEP * coveredDepth}`
        );
      });
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return { listRef, lockupRef };
}
